import { useState } from 'react';
import { useRightInset } from '../imagery/useRightInset';

/**
 * Coordinate readout for the cursor, in MGA or lat/lng.
 *
 * Previously this was a MapLibre control that borrowed the attribution
 * classes, so it read as fine print, and it offset itself from the right edge
 * by a hardcoded `20vw`. The sidebar is a fixed 336px, so that guess was wrong
 * at every window size except one - the readout drifted in and out of the
 * panel as the window changed.
 *
 * useRightInset measures the panels instead, which is the same thing the land
 * change legend does, so the two now sit on the same line and move together.
 *
 * Clicking it switches format. Both are needed and neither wins: MGA is what
 * you type into a handheld GPS and what a tenement application wants, lat/lng
 * is what you paste into a message or a phone. Making it a click on the
 * readout itself keeps a second control off the map, and the choice is
 * remembered per browser so nobody re-picks it every session.
 */

const FORMAT_KEY = 'goldfinder.cursor.format';
const FORMATS = ['mga', 'latlng'];

const readFormat = () => {
  try {
    const v = window.localStorage.getItem(FORMAT_KEY);
    return FORMATS.includes(v) ? v : 'mga';
  } catch { return 'mga'; }
};
const writeFormat = (v) => {
  try { window.localStorage.setItem(FORMAT_KEY, v); } catch { /* private mode */ }
};

const styles = {
  panel: {
    position: 'absolute',
    zIndex: 5,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    background: 'rgba(255,255,255,0.96)',
    borderRadius: 8,
    boxShadow: '0 2px 10px rgba(0,0,0,0.18)',
    padding: '6px 10px',
    border: 0,
    fontFamily: '"IBM Plex Mono", ui-monospace, monospace',
    fontVariantNumeric: 'tabular-nums',
    fontSize: 11.5,
    lineHeight: 1.2,
    color: 'var(--gf-secondary-700, #2D3238)',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    pointerEvents: 'auto',
  },
  tag: {
    fontFamily: 'var(--gf-font-sans, "Poppins", sans-serif)',
    fontSize: 9.5,
    fontWeight: 600,
    letterSpacing: '0.06em',
    color: 'var(--gf-primary-500, #E65A00)',
    background: 'var(--gf-primary-50, #FFF4ED)',
    borderRadius: 5,
    padding: '2px 5px',
  },
};

/**
 * `mga` and `latLng` are both supplied by the map; this only picks which to
 * show, so switching format never has to re-derive a projection.
 */
export function CursorPosition({ mga, latLng, bottomOffset = 10 }) {
  const rightInset = useRightInset();
  const [format, setFormat] = useState(readFormat);

  if (!mga && !latLng) return null;

  const toggle = () => {
    const next = format === 'mga' ? 'latlng' : 'mga';
    writeFormat(next);
    setFormat(next);
  };

  const showLatLng = format === 'latlng' && latLng;
  const text = showLatLng
    ? `lat: ${latLng.lat.toFixed(5)}, lng: ${latLng.lng.toFixed(5)}`
    : mga;

  return (
    <button
      type="button"
      onClick={toggle}
      style={{ ...styles.panel, right: rightInset + 12, bottom: bottomOffset }}
      title="Click to switch between MGA and latitude/longitude"
      aria-label={`Cursor position, ${showLatLng ? 'latitude and longitude' : 'MGA'}. Click to switch format.`}
    >
      <span style={styles.tag}>{showLatLng ? 'WGS84' : 'MGA'}</span>
      <span>{text}</span>
    </button>
  );
}

export default CursorPosition;
